import { useTranslate } from '@refinedev/core';
import type { CrudFilter } from '@refinedev/core';
import { List, EditButton } from '@refinedev/antd';
import { Table, Space, Input, Form, Row, Col } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import {
  OrganizationStatus,
  type OrganizationOut,
} from '@/api/generated/schemas';
import { useTableSearch } from '@/hooks/useTableSearch';
import { useTableRowNavigateToShow } from '@/hooks/useTableRowNavigateToShow';
import { useAdminTable } from '@/hooks/useAdminTable';
import { SelectFilter } from '@/components/common/SelectFilter';
import { DateRangeFilter } from '@/components/common/DateRangeFilter';
import { OrganizationStatusTag } from '@/components/tenancy/tags';
import { isFieldFiltered } from '@/utils/filters';
import { formatDateTime } from '@/utils/format';
import { getSortOrder } from '@/utils/sorters';

export const OrganizationList = () => {
  const t = useTranslate();
  const rowNav = useTableRowNavigateToShow();
  const { onSearch, createDebouncedSubmit } = useTableSearch();

  const { tableProps, searchFormProps, setFilters, filters, sorters } =
    useAdminTable<OrganizationOut>({
      pagination: { pageSize: 20 },
      resource: 'tenancy.organizations',
      syncWithLocation: true,
      onSearch,
      sorters: {
        initial: [{ field: 'created_at', order: 'desc' }],
      },
    });

  const debouncedSubmit = createDebouncedSubmit(searchFormProps.form);

  return (
    <List>
      <Form {...searchFormProps} layout="vertical">
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={16} md={12} lg={8}>
            <Form.Item name="q" style={{ marginBottom: 0 }}>
              <Input
                placeholder={t('tenancy.organizations.search.placeholder')}
                prefix={<SearchOutlined />}
                allowClear
                onChange={(e) => debouncedSubmit(e.target.value)}
              />
            </Form.Item>
          </Col>
        </Row>
      </Form>

      <Table {...rowNav('tenancy.organizations', tableProps)} rowKey="id">
        <Table.Column
          dataIndex="id"
          title={t('base.fields.id')}
          sorter
          sortOrder={getSortOrder(sorters, 'id')}
        />

        <Table.Column
          dataIndex="name"
          title={t('tenancy.organizations.fields.name')}
          sorter
          sortOrder={getSortOrder(sorters, 'name')}
        />

        <Table.Column
          dataIndex="status"
          title={t('tenancy.organizations.fields.status')}
          sorter
          sortOrder={getSortOrder(sorters, 'status')}
          render={(value: string) => <OrganizationStatusTag status={value} />}
          filtered={isFieldFiltered(filters as CrudFilter[], 'status')}
          filterDropdown={({ confirm, clearFilters }) => (
            <SelectFilter
              field="status"
              minWidth={160}
              placeholder={t('tenancy.organizations.fields.status')}
              options={Object.values(OrganizationStatus).map((value) => ({
                label: t(`tenancy.organizations.status.${value}`),
                value,
              }))}
              filters={filters as CrudFilter[]}
              confirm={confirm}
              clearFilters={clearFilters}
              setFilters={setFilters}
            />
          )}
        />

        <Table.Column
          dataIndex="members"
          title={t('tenancy.organizations.fields.members_count')}
          render={(members: OrganizationOut['members']) => members?.length ?? 0}
        />

        <Table.Column
          dataIndex="created_at"
          title={t('base.fields.created_at')}
          sorter
          sortOrder={getSortOrder(sorters, 'created_at')}
          filtered={isFieldFiltered(filters as CrudFilter[], 'created_at')}
          filterDropdown={({ confirm, clearFilters }) => (
            <DateRangeFilter
              field="created_at"
              filters={filters as CrudFilter[]}
              confirm={confirm}
              clearFilters={clearFilters}
              setFilters={setFilters}
            />
          )}
          render={(value: string) => formatDateTime(value)}
        />

        <Table.Column
          title={t('actions.column')}
          dataIndex="actions"
          render={(_, record: OrganizationOut) => (
            <Space>
              <EditButton hideText size="small" recordItemId={record.id} />
            </Space>
          )}
        />
      </Table>
    </List>
  );
};
